// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import { BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";

export const AuthContext = createContext();

// Small safe-parse helper used by every fetch call below.
// Prevents "Unexpected token '<'" crashes when the backend returns an HTML
// error page (404/500/proxy error/missing endpoint) instead of JSON.
const safeJsonParse = async (res) => {
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return null;
  }
  try {
    return await res.json();
  } catch (err) {
    console.error("Failed to parse JSON response:", err);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  // NEW: tracks whether the *initial* permissions fetch (on page load / login)
  // has completed. Starts `true` so consumers (like Sidebar) know to wait
  // before rendering permission-gated UI.
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // Stores { permissions: [], action_permissions: {} }
  const [permissions, setPermissions] = useState({
    permissions: [],
    action_permissions: {},
  });

  // Mirror of `permissions` kept in a ref so the background refresh can
  // compare an incoming payload against the current one *without* taking
  // `permissions` as a dependency (which would retrigger the refresh effect).
  const permissionsRef = useRef(permissions);
  useEffect(() => {
    permissionsRef.current = permissions;
  }, [permissions]);

  // `token` mirrored into a ref too. fetchPermissions is called from an
  // interval whose closure would otherwise capture a stale token.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // ---------------------------------------------
  // INIT — Load user + token from localStorage
  // ---------------------------------------------
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser) {
      // Wrapped JSON.parse in try/catch — if localStorage ever
      // contains corrupted/partial user JSON, this would previously throw
      // and crash the whole app on load. Now it fails quietly (no toast,
      // it's a background/init step) and simply skips restoring the session.
      let parsedUser = null;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch (err) {
        console.error("Failed to parse stored user:", err);
      }

      if (parsedUser) {
        setToken(storedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);

        // Fetch latest permissions. This will flip permissionsLoading to
        // false once it resolves (success or failure) — see fetchPermissions.
        fetchPermissions(parsedUser.role, parsedUser);
        return;
      }
    }

    // No valid session to restore — nothing to wait for.
    setPermissionsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------
  // LOGIN (user-initiated action — toasts kept)
  // ---------------------------------------------
  const login = async (email, password) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      // Use safeJsonParse instead of res.json() directly, so a
      // non-JSON error response (HTML error page, etc.) doesn't crash login.
      const data = await safeJsonParse(res);

      if (res.status === 200 && data && data.status === 200) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.data));

        setUser(data.data);
        setToken(data.token);
        setIsAuthenticated(true);
        setPermissionsLoading(true); // fresh login → wait for permissions again

        await fetchPermissions(data.data.role, data.data);

        toast.success(data.message || "Logged in successfully");
        return { success: true, data };
      } else {
        // Guard against data being null (non-JSON response) while
        // keeping the toast — this is a user-initiated login attempt, so a
        // failure toast is still appropriate per requirements.
        const msg = (data && data.message) || "Invalid credentials";
        toast.error(msg);
        return { success: false, message: msg };
      }
    } catch (error) {
      console.error(error);
      toast.error("Network error");
      return { success: false, message: "Network error" };
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------
  // FORGOT PASSWORD (user-initiated action — toasts kept)
  // ---------------------------------------------
  const forgotPassword = async (email) => {
    setIsLoading(true);
    setMessage("");
    try {
      const res = await fetch(`${BASE_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await safeJsonParse(res);

      if (data && data.status === 200) {
        setMessage("Password reset link sent to your email.");
        toast.success(data.message || "Password reset link sent to your email.");
        return {
          success: true,
          message: data.message,
          token: data.token,
        };
      } else {
        const msg = (data && data.message) || "Failed to send reset link.";
        setMessage(msg);
        toast.error(msg);
        return { success: false, data };
      }
    } catch (err) {
      setMessage("Something went wrong. Please try again.");
      toast.error("Something went wrong. Please try again.");
      return { success: false, data: err };
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------
  // LOGOUT (user-initiated action, no network call)
  // ---------------------------------------------
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setToken("");
    setIsAuthenticated(false);
    setPermissions({ permissions: [], action_permissions: {} });
    setPermissionsLoading(false); // no session → nothing to wait for
  };

  // ---------------------------------------------
  // FETCH PERMISSIONS (background — no toasts, unchanged behavior)
  // ---------------------------------------------
  const fetchPermissions = async (roleName, baseUser = null) => {
    if (!roleName) {
      setPermissionsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/permissions/${roleName}`, {
        // Read from tokenRef, not the captured `token`. The background
        // refresh runs from an interval whose closure would pin a stale token.
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      });

      // Safe parse — this runs on load and periodically in the
      // background, so a bad/HTML response must not throw or toast.
      const data = await safeJsonParse(res);

      if (data && data.status === 200) {
        // Skip no-op updates. Previously every background poll called
        // setUser() with a freshly-spread object plus a localStorage write, which
        // changed `user`'s identity, re-rendered the whole app tree and tore down
        // and rebuilt the refresh interval — on every single poll. Only apply the
        // update when the payload actually differs. `baseUser` is passed on
        // init/login, where we must always commit regardless.
        const unchanged =
          !baseUser &&
          JSON.stringify({
            permissions: data.permissions,
            action_permissions: data.action_permissions,
          }) === JSON.stringify(permissionsRef.current);

        if (unchanged) return;

        const finalUser = {
          ...(baseUser || user),
          permissions: data.permissions, // backward support
        };

        setUser(finalUser);

        // Store full res → includes both "permissions" & "action_permissions"
        setPermissions({
          permissions: data.permissions,
          action_permissions: data.action_permissions,
        });

        localStorage.setItem("user", JSON.stringify(finalUser));
      }
    } catch (error) {
      // No toast here (already none previously) — background fetch,
      // errors just logged, matches requirement of silent background failures.
      console.error("Failed to fetch permissions:", error);
    } finally {
      // Whether this was the initial load, a login, or a background poll,
      // once we've completed at least one attempt, we're no longer "loading"
      // for the purposes of gating first-render UI.
      setPermissionsLoading(false);
    }
  };

  // ---------------------------------------------
  // RESET PASSWORD (user-initiated action — toasts kept)
  // ---------------------------------------------
  const resetPassword = async (newPassword, token) => {
    setIsLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${BASE_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await safeJsonParse(res);

      if (data && data.status === 200) {
        toast.success(data.message || "Password reset successfully");
        return { success: true };
      } else {
        const msg = (data && data.message) || "Failed to reset password.";
        setMessage(msg);
        toast.error(msg);
        return { success: false };
      }
    } catch (err) {
      setMessage("Something went wrong. Please try again.");
      toast.error("Something went wrong. Please try again.");
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------
  // VERIFY OTP (user-initiated action — toasts kept)
  // ---------------------------------------------
  const verifyOTP = async (otp, token) => {
    try {
      const res = await fetch(`${BASE_URL}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp, token }),
      });

      const data = await safeJsonParse(res);

      if (data && data.status === 200) {
        toast.success(data.message || "OTP verified successfully");
      } else {
        toast.error((data && data.message) || "Failed to verify OTP");
      }

      return data || { success: false, message: "Invalid server response" };
    } catch (err) {
      toast.error("Server error");
      return { success: false, message: "Server error" };
    }
  };

  // Refresh permissions in the background (no toasts).
  //
  // Key on `user.role` (a string — stable across re-renders), refetch when
  // the tab regains focus, and keep a slow 5-minute poll as a safety net.
  const userRole = user?.role;
  useEffect(() => {
    if (!userRole) return;

    const tick = () => fetchPermissions(userRole);

    window.addEventListener("focus", tick);
    const interval = setInterval(tick, 300_000); // 5 minutes

    return () => {
      window.removeEventListener("focus", tick);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // ---------------------------------------------
  // PERMISSION HELPERS (Used everywhere)
  // ---------------------------------------------

  // Check if module is allowed in sidebar
  const hasModule = (moduleName) => {
    return permissions?.permissions?.includes(moduleName);
  };

  // Check if an action is allowed (Add/Edit/Delete/view)
  const canDo = (moduleName, action) => {
    return permissions?.action_permissions?.[moduleName]?.includes(action);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        isAuthenticated,
        permissions,
        permissionsLoading, // NEW: expose to consumers like Sidebar
        fetchPermissions,
        forgotPassword,
        verifyOTP,
        resetPassword,
        // Permission helper functions
        hasModule,
        canDo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);